// assets/product-media-lightbox.js

class ProductMediaLightbox extends HTMLElement {
  constructor() {
    super();
    this.lightbox = this.querySelector('#' + this.getAttribute('data-lightbox-id'));
    this.mediaContainer = this.lightbox.querySelector('.product-media-lightbox__media-container');
    this.closeButton = this.lightbox.querySelector('.product-media-lightbox__close');
    this.overlay = this.lightbox.querySelector('.product-media-lightbox__overlay');
    this.prevButton = this.lightbox.querySelector('.product-media-lightbox__nav--prev');
    this.nextButton = this.lightbox.querySelector('.product-media-lightbox__nav--next');
    this.thumbnailsContainer = this.lightbox.querySelector('.product-media-lightbox__thumbnails');
    
    // Get existing counter or create a new one if it doesn't exist
    // this.counter = this.lightbox.querySelector('.product-media-lightbox__counter');
    // if (!this.counter) {
    //   this.counter = document.createElement('div');
    //   this.counter.className = 'product-media-lightbox__counter';
    //   this.lightbox.appendChild(this.counter);
    // }
    
    this.liveRegion = this.lightbox.querySelector('[aria-live]');

    this.mediaItems = [];
    this.currentIndex = 0;
    this.activeElement = null; // To store focus before opening
    this.thumbnails = [];
    this.hideVariants = true; // Default to hiding variants, like the product page

    // Set initial aria-hidden on all body children except this lightbox
    this.bodyChildren = Array.from(document.body.children).filter(child => !child.contains(this.lightbox));

    this.initMediaItems();
    this.bindEvents();
  }

  initMediaItems() {
    const mediaDataAttribute = this.getAttribute('data-media-json');
    const variantImages = this.getAttribute('data-variant-images');
    let variantImagesList = [];
    
    if (variantImages) {
      try {
        variantImagesList = JSON.parse(variantImages);
      } catch (e) {
        console.error('Error parsing variant images:', e);
      }
    }
    
    this.hideVariants = this.getAttribute('data-hide-variants') === 'true';
    
    if (mediaDataAttribute) {
      try {
        // Parse all media items
        let allMedia = JSON.parse(mediaDataAttribute);
        
        // Filter out variant images if hideVariants is true
        if (this.hideVariants && variantImagesList.length > 0) {
          this.mediaItems = allMedia.filter(media => {
            // Skip if this is a variant image
            const isVariantImage = variantImagesList.includes(media.src) || 
                                   variantImagesList.includes(media.id);
            return !isVariantImage;
          });
        } else {
          this.mediaItems = allMedia;
        }
        
        // Generate thumbnails once we have the media data
        this.generateThumbnails();
        
        // Initialize counter
        // this.updateCounter(); // Counter removed
      } catch (e) {
        console.error('Error parsing product media JSON:', e);
        this.mediaItems = [];
      }
    } else {
      console.warn('Lightbox host element missing data-media-json attribute.');
    }
  }

  generateThumbnails() {
    if (!this.thumbnailsContainer || !this.mediaItems.length) return;
    
    // Clear any existing thumbnails
    this.thumbnailsContainer.innerHTML = '';
    this.thumbnails = [];
    
    // Create a thumbnail for each media item
    this.mediaItems.forEach((media, index) => {
      const thumbnailElement = document.createElement('div');
      thumbnailElement.className = 'product-media-lightbox__thumbnail';
      thumbnailElement.setAttribute('data-index', index);
      thumbnailElement.setAttribute('tabindex', '0');
      thumbnailElement.setAttribute('role', 'button');
      thumbnailElement.setAttribute('aria-label', `View image ${index + 1} of ${this.mediaItems.length}`);
      
      // Add image to thumbnail
      const thumbnailImage = document.createElement('img');
      thumbnailImage.src = media.preview_image?.src || media.src;
      thumbnailImage.alt = '';
      thumbnailImage.loading = 'lazy';
      
      thumbnailElement.appendChild(thumbnailImage);
      this.thumbnailsContainer.appendChild(thumbnailElement);
      this.thumbnails.push(thumbnailElement);
      
      // Add click event to thumbnail
      thumbnailElement.addEventListener('click', () => this.goToMedia(index));
      thumbnailElement.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.goToMedia(index);
        }
      });
    });
  }

  updateActiveThumbnail() {
    // Remove active class from all thumbnails
    this.thumbnails.forEach(thumbnail => {
      thumbnail.classList.remove('active');
    });
    
    // Add active class to current thumbnail
    if (this.thumbnails[this.currentIndex]) {
      this.thumbnails[this.currentIndex].classList.add('active');
      
      // Scroll the thumbnail into view with smooth animation
      this.scrollThumbnailIntoView(this.thumbnails[this.currentIndex]);
    }
  }
  
  scrollThumbnailIntoView(thumbnail) {
    if (!thumbnail || !this.thumbnailsContainer) return;
    
    // Don't always center - only scroll if thumbnail would be outside visible area
    const containerRect = this.thumbnailsContainer.getBoundingClientRect();
    const thumbnailRect = thumbnail.getBoundingClientRect();
    
    // Calculate if thumbnail is outside the visible area (either left or right)
    const isOutsideLeft = thumbnailRect.left < containerRect.left;
    const isOutsideRight = thumbnailRect.right > containerRect.right;
    
    // Only scroll if the thumbnail is not fully visible
    if (isOutsideLeft || isOutsideRight) {
      // If it's outside to the left, scroll to make it visible on the left
      if (isOutsideLeft) {
        this.thumbnailsContainer.scrollLeft += thumbnailRect.left - containerRect.left - 10; // 10px buffer
      }
      // If it's outside to the right, scroll to make it visible on the right
      else if (isOutsideRight) {
        this.thumbnailsContainer.scrollLeft += thumbnailRect.right - containerRect.right + 10; // 10px buffer
      }
    }
  }

  bindEvents() {
    this.lightboxId = this.getAttribute('data-lightbox-id');
    if (!this.lightboxId) {
        console.error('Lightbox host is missing data-lightbox-id attribute.');
        return;
    }

    // Delegation for all potential trigger elements
    document.addEventListener('click', this.handleClick.bind(this));

    // Handle keyboard events
    document.addEventListener('keydown', this.handleGlobalKeydown.bind(this));

    this.closeButton.addEventListener('click', this.close.bind(this));
    this.overlay.addEventListener('click', this.close.bind(this));
    this.prevButton.addEventListener('click', this.prevMedia.bind(this));
    this.nextButton.addEventListener('click', this.nextMedia.bind(this));
    
    // Focus trap in lightbox
    this.lightbox.addEventListener('keydown', this.handleKeydown.bind(this));
    
    // Touch swipe support for mobile devices
    this.setupSwipeHandling();
    
    // Handle window resize to adjust lightbox
    window.addEventListener('resize', this.handleResize.bind(this));
  }
  
  handleClick(event) {
    // Check if clicked element or its parent is a trigger for this lightbox
    const triggerElement = event.target.closest(`.product-media-container[aria-controls="${this.lightboxId}"]`) || 
                          event.target.closest(`.product-media__trigger-lightbox[aria-controls="${this.lightboxId}"]`);
    
    if (triggerElement) {
        event.preventDefault();
        event.stopPropagation();
        
        // Get media ID
        const mediaId = triggerElement.getAttribute('data-media-id') || 
                      triggerElement.closest('[data-media-id]')?.getAttribute('data-media-id');
        
        if (mediaId) {
            this.open(mediaId);
        }
    }
  }
  
  handleGlobalKeydown(event) {
    if (!this.lightbox.hasAttribute('open')) return;
    
    if (event.key === 'Escape') {
        this.close();
    } else if (event.key === 'ArrowLeft') {
        this.prevMedia();
    } else if (event.key === 'ArrowRight') {
        this.nextMedia();
    }
  }

  setupSwipeHandling() {
    let touchStartX = 0;
    let touchEndX = 0;
    const minSwipeDistance = 50;
    
    this.lightbox.addEventListener('touchstart', (e) => {
        touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });
    
    this.lightbox.addEventListener('touchend', (e) => {
        touchEndX = e.changedTouches[0].screenX;
        this.handleSwipe(touchStartX, touchEndX, minSwipeDistance);
    }, { passive: true });
  }
  
  handleSwipe(startX, endX, minDistance) {
    const swipeDistance = endX - startX;
    
    if (Math.abs(swipeDistance) >= minDistance) {
        if (swipeDistance > 0) {
            // Swiped right - go to previous
            this.prevMedia();
        } else {
            // Swiped left - go to next
            this.nextMedia();
        }
    }
  }
  
  handleResize() {
    if (!this.lightbox.hasAttribute('open')) return;

    // Adjust any size-dependent elements if needed
    // this.adjustMediaSize(); // No longer needed, CSS handles sizing

    // Re-center the active thumbnail after resize
    if (this.thumbnails[this.currentIndex]) {
      this.scrollThumbnailIntoView(this.thumbnails[this.currentIndex]);
    }
  }

  adjustMediaSize() {
    // This function is now largely redundant as CSS handles object-fit: contain
    // within the flex container. Leaving the structure in case fine-tuning is needed later.
    /*
    const currentMedia = this.mediaContainer.querySelector('img, video, model-viewer');
    if (currentMedia) {
      // CSS handles sizing now
    }
    */
  }

  open(mediaId) {
    if (!this.mediaItems.length) return;

    // Find the media index, accounting for variant filtering
    let initialIndex = this.mediaItems.findIndex(item => item.id == mediaId);
    
    // If not found, use the first media item
    this.currentIndex = initialIndex !== -1 ? initialIndex : 0;

    this.activeElement = document.activeElement; // Save focus
    this.lightbox.removeAttribute('hidden');
    this.lightbox.setAttribute('open', '');
    document.documentElement.style.overflow = 'hidden'; // Lock scroll on HTML
    document.body.style.overflow = 'hidden'; // Prevent background scroll
    
    // Improve accessibility by hiding other content from screen readers
    this.bodyChildren.forEach(child => {
      child.setAttribute('aria-hidden', 'true');
    });

    // Render the media immediately
    this.renderMedia();
    this.updateNavButtons();
    // this.updateCounter(); // Counter removed
    this.updateActiveThumbnail();

    // Trap focus
    this.setFocusToFirstNode();
    
    // Announce to screen readers using the visually hidden element
    if (this.liveRegion) {
      const mediaItem = this.mediaItems[this.currentIndex];
      const altText = mediaItem.alt || `Product image ${this.currentIndex + 1} of ${this.mediaItems.length}`;
      this.liveRegion.textContent = `Lightbox opened showing ${altText}`;
    }
  }

  close() {
    this.pauseMedia();
    this.lightbox.setAttribute('hidden', '');
    this.lightbox.removeAttribute('open');
    document.documentElement.style.overflow = ''; // Unlock scroll on HTML
    document.body.style.overflow = '';
    this.mediaContainer.innerHTML = ''; // Clear content
    
    // Restore aria-hidden attributes
    this.bodyChildren.forEach(child => {
      child.removeAttribute('aria-hidden');
    });
    
    // Re-add the live region
    const liveRegion = document.createElement('span');
    liveRegion.className = 'visually-hidden';
    liveRegion.setAttribute('aria-live', 'polite');
    this.mediaContainer.appendChild(liveRegion);
    this.liveRegion = liveRegion;

    // Focus the body instead of the active element
    document.body.focus();
    
    // Original focus restoration code - commented out
    // if (this.activeElement) {
    //     this.activeElement.focus();
    // }
  }

  renderMedia() {
    if (this.currentIndex < 0 || this.currentIndex >= this.mediaItems.length) return;

    this.pauseMedia(); // Pause any currently playing media before switching
    this.mediaContainer.innerHTML = ''; // Clear previous media except the live region
    
    // Re-add the live region so it's always present
    const liveRegion = document.createElement('span');
    liveRegion.className = 'visually-hidden';
    liveRegion.setAttribute('aria-live', 'polite');
    this.mediaContainer.appendChild(liveRegion);
    this.liveRegion = liveRegion;

    const mediaItem = this.mediaItems[this.currentIndex];
    let element;

    switch (mediaItem.media_type) {
      case 'image':
        element = document.createElement('img');
        element.src = mediaItem.src; // Consider using a larger image size if available
        element.alt = mediaItem.alt || 'Product Image';
        element.loading = 'eager'; // Load eagerly as it's in the lightbox
        break;
      case 'video':
      case 'external_video':
        element = document.createElement('deferred-media');
        element.setAttribute('data-media-id', mediaItem.id);
        element.style.width = '100%';
        element.style.height = '100%';
        // Recreate the template structure expected by deferred-media
        const template = document.createElement('template');
        const videoButton = document.createElement('button');
        videoButton.id = `Deferred-Poster-${mediaItem.id}`;
        videoButton.className = 'deferred-media__poster motion-reduce'
        videoButton.type = 'button';

        const posterImg = document.createElement('img');
        posterImg.src = mediaItem.preview_image.src;
        posterImg.alt = mediaItem.alt || 'Load video';
        posterImg.loading = 'eager';
        posterImg.width = mediaItem.preview_image.width;
        posterImg.height = mediaItem.preview_image.height;

        const playIcon = document.createElement('span');
        playIcon.className = 'deferred-media__poster-button motion-reduce-hide';
        // Ideally, render an SVG icon here, similar to product-media snippet
        playIcon.innerHTML = `<svg height="100%" version="1.1" viewBox="0 0 36 36" width="100%"><path d="M 12,26 18.5,22 18.5,14 12,10 z M 18.5,22 25,18 25,18 18.5,14 z" fill="#ffffff"></path></svg>`;

        videoButton.appendChild(posterImg);
        videoButton.appendChild(playIcon);
        template.content.appendChild(videoButton);

        // Create the iframe structure inside the template
        const noscript = document.createElement('noscript');
        const videoDiv = document.createElement('div');
        videoDiv.className = `deferred-media__content ${mediaItem.media_type === 'external_video' ? 'js-youtube' : 'js-mp4'}`;
        videoDiv.innerHTML = mediaItem.sources.map(s =>
            mediaItem.media_type === 'external_video'
            ? `<iframe frameborder="0" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowfullscreen src="https://www.youtube.com/embed/${mediaItem.external_id}?autoplay=1&enablejsapi=1&playsinline=1&controls=1&rel=0&modestbranding=1"></iframe>`
            : `<video playsinline controls loop><source src="${s.url}" type="${s.mime_type}"></video>`
        ).join(''); // Handle both video types
        noscript.appendChild(videoDiv);
        template.content.appendChild(noscript);

        element.appendChild(template);
        break;
      case 'model':
        element = document.createElement('product-model');
        element.setAttribute('data-media-id', mediaItem.id);
        element.style.width = '100%';
        element.style.height = '100%';
        // Recreate the template structure expected by product-model
        const modelTemplate = document.createElement('template');
        const modelViewer = document.createElement('model-viewer');
        // Add necessary attributes to model-viewer based on mediaItem properties
        modelViewer.setAttribute('src', mediaItem.sources.find(s => s.format === 'glb').url);
        modelViewer.setAttribute('alt', mediaItem.alt || '3D model');
        modelViewer.setAttribute('camera-controls', '');
        modelViewer.setAttribute('ar', '');
        modelViewer.setAttribute('ar-modes', 'webxr scene-viewer quick-look');
        modelViewer.setAttribute('interaction-prompt', 'auto');
        modelViewer.setAttribute('touch-action', 'pan-y');
        modelViewer.setAttribute('xr-environment', '');
        modelViewer.setAttribute('poster', mediaItem.preview_image.src);
        modelViewer.setAttribute('loading', 'eager');
        modelViewer.setAttribute('reveal', 'auto');
        modelTemplate.content.appendChild(modelViewer);
        element.appendChild(modelTemplate);
        break;
      default:
        console.warn('Unsupported media type in lightbox:', mediaItem.media_type);
        return;
    }

    this.mediaContainer.appendChild(element);

    // Add navigation status for accessibility (visually hidden)
    const statusText = document.createElement('span');
    statusText.className = 'visually-hidden';
    statusText.setAttribute('aria-hidden', 'true');  // Don't announce this additionally
    statusText.textContent = `Item ${this.currentIndex + 1} of ${this.mediaItems.length}`;
    this.mediaContainer.appendChild(statusText);

    // Load deferred media (videos, models)
    if (element.tagName === 'DEFERRED-MEDIA' || element.tagName === 'PRODUCT-MODEL') {
        // Need a slight delay for the element to be fully in the DOM
        setTimeout(() => element.loadContent && element.loadContent(), 50);
    }
    
    // Apply any size adjustments
    this.adjustMediaSize();
  }

  pauseMedia() {
    const currentVideo = this.mediaContainer.querySelector('video, iframe');
    if (currentVideo) {
      if (currentVideo.tagName === 'VIDEO' && !currentVideo.paused) {
        currentVideo.pause();
      } else if (currentVideo.tagName === 'IFRAME' && currentVideo.contentWindow && typeof currentVideo.contentWindow.postMessage === 'function') {
        // Try to pause YouTube/Vimeo videos
        currentVideo.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
        currentVideo.contentWindow.postMessage('{"method":"pause","value":"true"}', '*');
      }
    }
    // Pause 3D model animations if applicable
    const currentModel = this.mediaContainer.querySelector('model-viewer');
    if (currentModel && typeof currentModel.pause === 'function') {
      try {
        currentModel.pause();
      } catch (e) {
        // Silent fail if method not supported
      }
    }
  }

  prevMedia() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.renderMedia();
      this.updateNavButtons();
      // this.updateCounter(); // Counter removed
      this.updateActiveThumbnail();
    }
  }

  nextMedia() {
    if (this.currentIndex < this.mediaItems.length - 1) {
      this.currentIndex++;
      this.renderMedia();
      this.updateNavButtons();
      // this.updateCounter(); // Counter removed
      this.updateActiveThumbnail();
    }
  }
  
  goToMedia(index) {
    if (index >= 0 && index < this.mediaItems.length && index !== this.currentIndex) {
      this.currentIndex = index;
      this.renderMedia();
      this.updateNavButtons();
      // this.updateCounter(); // Counter removed
      this.updateActiveThumbnail();
    }
  }

  updateNavButtons() {
    this.prevButton.disabled = this.currentIndex === 0;
    this.nextButton.disabled = this.currentIndex === this.mediaItems.length - 1;
    
    // Update aria-labels for better accessibility
    this.prevButton.setAttribute('aria-label', 
      this.currentIndex === 0 ? 'Previous image (disabled)' : 'Previous image');
    this.nextButton.setAttribute('aria-label', 
      this.currentIndex === this.mediaItems.length - 1 ? 'Next image (disabled)' : 'Next image');
  }

  handleKeydown(event) {
    if (event.key === 'Tab') {
        // Basic focus trapping
        const focusableElements = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
        const focusableContent = this.lightbox.querySelectorAll(focusableElements);
        const firstFocusableElement = focusableContent[0];
        const lastFocusableElement = focusableContent[focusableContent.length - 1];

        if (event.shiftKey) { // Shift + Tab
            if (document.activeElement === firstFocusableElement) {
                lastFocusableElement.focus();
                event.preventDefault();
            }
        } else { // Tab
            if (document.activeElement === lastFocusableElement) {
                firstFocusableElement.focus();
                event.preventDefault();
            }
        }
    }
  }

  setFocusToFirstNode() {
    // Focus the close button first for better user experience
    if (this.closeButton) {
      this.closeButton.focus();
    } else {
      const focusableElements = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
      const firstFocusableElement = this.lightbox.querySelector(focusableElements);
      if (firstFocusableElement) {
        firstFocusableElement.focus();
      }
    }
  }
}

if (!customElements.get('product-media-lightbox-host')) {
    customElements.define('product-media-lightbox-host', ProductMediaLightbox);
}

// Initialize all media container elements to show zoom cursor
document.addEventListener('DOMContentLoaded', () => {
  // Ensure all media containers are visible and have the zoom cursor
  document.querySelectorAll('.product-media-container').forEach(container => {
    if (container.classList.contains('media-type-image')) {
      container.style.cursor = 'zoom-in';
    }
  });
  
  // Make sure all trigger icons are visible
  document.querySelectorAll('.product-media__trigger-lightbox').forEach(trigger => {
    trigger.style.pointerEvents = 'auto';
  });
}); 